'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import classNames from 'classnames';
import { Spinner } from '@benzinga/core-ui';
import { StyledLine } from '../Table';

export interface TableContainerProps {
  alignLastRowsRight?: boolean;
  hasStockTableStyling?: boolean;
  mobileHeight?: number;
  children: React.ReactNode;
  className?: string;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  height?: number;
  isLoading?: boolean;
  loadingText?: string;
  seeMoreLink?: string;
  seeMoreText?: string;
  title?: string;
}

export const TableContainer: React.FC<TableContainerProps> = ({
  alignLastRowsRight,
  children,
  className,
  containerRef,
  hasStockTableStyling,
  height,
  isLoading,
  loadingText,
  mobileHeight,
  seeMoreLink,
  seeMoreText,
  title,
}) => {
  return (
    <Container
      $alignLastRowsRight={alignLastRowsRight}
      $hasStockTableStyling={hasStockTableStyling}
      $height={height}
      $mobileHeight={mobileHeight}
      className={classNames('benzinga-core-table-container w-full', className)}
      ref={containerRef}
    >
      {title && (
        <div className="header-container flex items-center">
          <h3 className="header-title">{title}</h3>
          <StyledLine />
          {seeMoreLink && (
            <a className="see-more-link" href={seeMoreLink} rel="noreferrer" target="_blank">
              See More
              {seeMoreText && <span className="sr-only">{seeMoreText}</span>}
            </a>
          )}
        </div>
      )}
      <div className="relative">
        {isLoading ? (
          <div className="loader-element flex flex-col items-center justify-center w-full" style={{ height: height }}>
            <Spinner />
            {loadingText && <div className="text-gray-500 text-sm mt-3">{loadingText}</div>}
          </div>
        ) : (
          children
        )}
      </div>
    </Container>
  );
};

const Container = styled.div<{
  $alignLastRowsRight?: boolean;
  $hasStockTableStyling?: boolean;
  $height?: number;
  $mobileHeight?: number;
}>`
  &.benzinga-core-table-container {
    .header-container {
      margin-bottom: 0.75rem;

      .header-title {
        z-index: 2;
        font-weight: 600;
        font-size: ${({ theme }) => theme.fontSize.base};
        line-height: 1;
        padding-right: 0.5rem;
        word-wrap: break-word;
        white-space: normal;
      }

      .see-more-link {
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        font-size: ${({ theme }) => theme.fontSize.sm};
        color: ${({ theme }) => theme.colorPalette.blue500};
        margin-left: 0.625rem;
      }
    }

    .benzinga-core-table-wrapper {
      max-height: ${({ $height }) => ($height ? `${$height}px` : 'unset')};

      @media (max-width: 640px) {
        max-height: ${({ $height, $mobileHeight }) =>
          $mobileHeight ? `${$mobileHeight}px` : $height ? `${$height}px` : 'unset'};
      }
    }

    .loader-element {
      max-height: ${({ $height }) => ($height ? `${$height}px` : 'unset')};
      @media (max-width: 640px) {
        max-height: ${({ $height, $mobileHeight }) =>
          $mobileHeight ? `${$mobileHeight}px` : $height ? `${$height}px` : 'unset'};
      }
    }

    table {
      th,
      td {
        padding: ${({ $hasStockTableStyling }) => ($hasStockTableStyling ? '0.2rem 0.5rem' : '0.35rem 0.5rem')};
      }
      td {
        &:last-of-type {
          text-align: ${({ $alignLastRowsRight }) => ($alignLastRowsRight ? 'right' : 'unset')};
        }
      }
    }
  }
`;
